import { type FC, useCallback, useMemo } from 'react'
import Sidebar, { SidebarType } from '../Sidebar'
import VoiceBoxListFilters from './VoiceBoxListFilters'
import VoiceBox from './VoiceBox'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { fetchVoices } from '@/features/voice/domain/use-cases/fetch-voices.ts'
import useUiStore from '../../../shared/store/ui.store.tsx'
import CardPlaceholder from '../../common/CardPlaceholder.tsx'
import { QueryKeys } from '@/utils/query-keys.ts'
import type { VoiceParameters } from '@/features/voice/domain/models/voice-parameters.ts'
import { useShallow } from 'zustand/react/shallow'
import { useVoiceFiltersStore } from '@/features/voice/store/voice-filters.store'

const VoiceBoxList: FC = (props) => {
  const { setIsSidebarOpen, setSidebarType, onVoiceSelected, selectVoice } =
    useUiStore(
      useShallow((state) => ({
        setIsSidebarOpen: state.setIsSidebarOpen,
        setSidebarType: state.setSidebarType,
        onVoiceSelected: state.onVoiceSelected,
        selectVoice: state.selectVoice,
      }))
    )
  const { filters, searchTerm } = useVoiceFiltersStore()

  const { data, fetchNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: [QueryKeys.Voices, filters, searchTerm],
      queryFn: async ({ pageParam = 0 }) =>
        await fetchVoices(pageParam, filters, searchTerm),
      getNextPageParam: (lastPage, pages) =>
        lastPage.page < lastPage.totalPages ? pages.length : undefined,
      initialPageParam: 0,
    })

  const { data: parametersData } = useQuery({
    queryKey: [QueryKeys.Voices, 'parameters', searchTerm],
    queryFn: () => fetchVoices(0, {}, searchTerm),
    staleTime: Infinity,
  })

  const voiceData = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  )
  const parameters: VoiceParameters | undefined = parametersData?.parameters
  const noResults = !isLoading && !isFetchingNextPage && voiceData.length === 0

  const handleSearch = useCallback((term: string) => {
    useVoiceFiltersStore.setState({ searchTerm: term })
  }, [])

  const handleSetFilters = useCallback(
    (updater: (prevFilters: typeof filters) => typeof filters) => {
      useVoiceFiltersStore.setState((state) => ({
        filters: updater(state.filters),
      }))
    },
    []
  )

  return (
    <Sidebar
      {...props}
      title="ls_sidebar_voices_title"
      searchKey="ls_sidebar_voices_search"
      searchAction={handleSearch}
      initialSearchTerm={searchTerm}
      fetchMoreData={fetchNextPage}
      noResults={noResults}
      goBack={() => setSidebarType(SidebarType.Scripts)}
    >
      <div className="flex flex-col gap-4 bg-zinc-900 p-4 pr-6">
        <VoiceBoxListFilters
          setFilters={handleSetFilters}
          parameters={parameters}
          filters={filters}
        />
        <div className="flex flex-col gap-2">
          {voiceData.map((voice, index) => (
            <VoiceBox
              key={voice.id ?? index}
              voice={voice}
              onSelected={() => {
                if (onVoiceSelected) {
                  selectVoice(voice)
                }
                setIsSidebarOpen(false)
              }}
            />
          ))}
          {(isLoading || isFetchingNextPage) &&
            Array.from({ length: 5 }, (_, index) => (
              <CardPlaceholder key={index} height={156} />
            ))}
        </div>
      </div>
    </Sidebar>
  )
}

export default VoiceBoxList
